<template>
  <div class="button-permission-example">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>按钮权限示例</span>
      </div>
      
      <h3>基本按钮权限控制</h3>
      <div class="button-container">
        <!-- 使用v-permission指令控制按钮的显示 -->
        <el-button type="primary" v-permission="'user:add'">添加用户</el-button>
        <el-button type="success" v-permission="'user:edit'">编辑用户</el-button>
        <el-button type="danger" v-permission="'user:delete'">删除用户</el-button>
        <el-button type="warning" v-permission="'user:export'">导出用户</el-button>
        
        <!-- 这个按钮总是显示 -->
        <el-button type="info">查看用户（无需权限）</el-button>
      </div>
      
      <el-divider></el-divider>
      
      <h3>表格中的按钮权限控制</h3>
      <el-table :data="tableData" style="width: 100%">
        <el-table-column prop="name" label="用户名" width="180"></el-table-column>
        <el-table-column prop="role" label="角色" width="180"></el-table-column>
        <el-table-column label="操作">
          <template slot-scope="scope">
            <el-button size="mini" v-permission="'user:edit'" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" v-permission="'user:delete'" @click="handleDelete(scope.row)">删除</el-button>
            <el-button size="mini" type="success" v-permission="'user:role'" @click="handleRole(scope.row)">角色分配</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <el-divider></el-divider>
      
      <h3>下拉菜单中的权限控制</h3>
      <el-dropdown>
        <el-button type="primary">
          更多操作<i class="el-icon-arrow-down el-icon--right"></i>
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item v-permission="'user:add'">添加用户</el-dropdown-item>
          <el-dropdown-item v-permission="'user:import'">导入用户</el-dropdown-item>
          <el-dropdown-item v-permission="'user:export'">导出用户</el-dropdown-item>
          <el-dropdown-item>刷新列表</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
      
      <el-divider></el-divider>
      
      <h3>使用程序判断权限</h3>
      <div class="button-container">
        <el-button type="primary" @click="handleAction('user:add')">添加用户</el-button>
        <el-button type="danger" @click="handleAction('user:delete')">删除用户</el-button>
      </div>
      
      <div class="info-container">
        <h3>使用说明：</h3>
        <ol>
          <li>
            <strong>指令方式：</strong> 使用 <code>v-permission="'permission:code'"</code> 指令控制元素的显示。
            <pre><code>&lt;el-button v-permission="'user:add'"&gt;添加用户&lt;/el-button&gt;</code></pre>
          </li>
          <li>
            <strong>程序方式：</strong> 使用 <code>this.hasButtonPermission('permission:code')</code> 方法判断权限。
            <pre><code>if (this.hasButtonPermission('user:add')) {
  // 执行添加操作
}</code></pre>
          </li>
          <li>
            <strong>权限码格式：</strong> 建议使用 <code>资源:操作</code> 格式，如 <code>user:add</code>, <code>user:edit</code>
          </li>
          <li>
            <strong>权限管理：</strong> 在菜单管理页面可以配置按钮权限，并为用户分配权限
          </li>
        </ol>
      </div>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'ButtonPermissionExample',
  data() {
    return {
      tableData: [
        { id: 1, name: '张三', role: '管理员' },
        { id: 2, name: '李四', role: '运营人员' },
        { id: 3, name: '王五', role: '普通用户' }
      ]
    }
  },
  methods: {
    // 处理编辑操作
    handleEdit(row) {
      this.$message.success(`正在编辑用户: ${row.name}`);
    },
    
    // 处理删除操作
    handleDelete(row) {
      this.$confirm(`确认删除用户 ${row.name}?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$message.success(`已删除用户: ${row.name}`);
      }).catch(() => {
        this.$message.info('已取消删除');
      });
    },
    
    // 处理角色分配
    handleRole(row) {
      this.$message.success(`正在为用户 ${row.name} 分配角色`);
    },
    
    // 使用程序判断权限并执行操作
    handleAction(permissionCode) {
      // 从 sessionStorage 获取按钮权限列表
      const buttonPermissionsStr = sessionStorage.getItem('buttonPermissions');
      if (!buttonPermissionsStr) {
        this.$message.error('无法获取权限信息');
        return;
      }
      
      try {
        const buttonPermissions = JSON.parse(buttonPermissionsStr);
        if (buttonPermissions.includes(permissionCode)) {
          // 有权限，执行操作
          if (permissionCode === 'user:add') {
            this.$message.success('正在执行添加用户操作');
          } else if (permissionCode === 'user:delete') {
            this.$message.success('正在执行删除用户操作');
          }
        } else {
          // 无权限，提示错误
          this.$message.error(`您没有 ${permissionCode} 权限`);
        }
      } catch (error) {
        console.error('解析按钮权限数据失败:', error);
        this.$message.error('解析权限数据失败');
      }
    },
    
    // 检查是否有指定按钮权限
    hasButtonPermission(permissionCode) {
      if (!permissionCode) return false;
      
      // 从 sessionStorage 获取按钮权限列表
      const buttonPermissionsStr = sessionStorage.getItem('buttonPermissions');
      if (!buttonPermissionsStr) return false;
      
      try {
        const buttonPermissions = JSON.parse(buttonPermissionsStr);
        return buttonPermissions.includes(permissionCode);
      } catch (error) {
        console.error('解析按钮权限数据失败:', error);
        return false;
      }
    }
  }
}
</script>

<style scoped>
.button-permission-example {
  padding: 20px;
}

.button-container {
  margin: 20px 0;
}

.button-container .el-button {
  margin-right: 10px;
  margin-bottom: 10px;
}

.info-container {
  margin-top: 30px;
  padding: 15px;
  background-color: #f8f8f8;
  border-radius: 4px;
}
</style>
